import { useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';
import { type Address } from 'viem';

import { type FeasibilityResponse, type MutationExecuteStepArgs } from '@pkg/graphql/codegen/graphql';
import { getCurrentStepItems } from '@pkg/hooks/marketplace/utils/getOrderStepItems';
import { StepKind } from '@pkg/types';

import { callExecuteOrderStep } from '@app/client/resources/marketplace';

import { useSignature, useTransaction } from '../walletInteractions';

export function useExecuteStep() {
    const sendTransaction = useTransaction();
    const getSignature = useSignature();

    const { mutateAsync: stepMutation } = useMutation({
        mutationFn: ({ variables }: { variables: MutationExecuteStepArgs }) => callExecuteOrderStep({ variables }),
    });

    return useCallback(
        async (account: Address, prepareOrderResponse: FeasibilityResponse) => {
            const allStepsWithPendingItems = getCurrentStepItems(prepareOrderResponse.origin.steps);
            const promises = allStepsWithPendingItems.map(async (item) => {
                if (item.kind === StepKind.TRANSACTION) {
                    return await sendTransaction(item);
                }
                if (item.kind === StepKind.SIGNATURE) {
                    const signature = await getSignature(item, account);
                    if (signature) {
                        const step = item.data?.post?.endpoint?.includes('auth-signature')
                            ? {
                                  reservoirAuthSignature: {
                                      signature,
                                      body: item.data.post.body,
                                  },
                              }
                            : {
                                  reservoirSubmitOrder: {
                                      signature,
                                      body: item.data.post.body,
                                  },
                              };
                        return stepMutation({
                            variables: {
                                orderId: prepareOrderResponse.orderId,
                                step,
                            },
                        });
                    }
                }
            });
            if (account && promises?.length) {
                const results = await Promise.allSettled(promises);

                if (results) {
                    const rejectionWithReason = results.find(
                        (r) => r.status === 'rejected' && (r.reason?.shortMessage?.length || r.reason?.details?.length),
                    ) as PromiseRejectedResult;
                    if (rejectionWithReason) {
                        throw new Error(rejectionWithReason.reason.shortMessage || rejectionWithReason.reason.details);
                    }
                    return results.some((result) => result.status === 'fulfilled');
                }
            }
        },
        [getSignature, sendTransaction, stepMutation],
    );
}
